import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { port } from '../App';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';

const GlobalEnquiryModal = ({ isOpen, onClose, sourcePage, customFields }) => {
    const defaultFields = [
        "Full Name", 
        "Company Name", 
        "Business Email", 
        "Phone Number", 
        "Workforce Goal", 
        "Message"
    ];
    
    const displayFields = customFields && customFields.length > 0 ? customFields : defaultFields;
    const regularFields = displayFields.filter(f => f.toLowerCase() !== 'message');
    const hasMessage = displayFields.some(f => f.toLowerCase() === 'message');

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'Phone Number') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation: Phone Number must be 10 digits
        if (formData['Phone Number'] && formData['Phone Number'].length !== 10) {
            toast.error("Phone number must be exactly 10 digits.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                source_page: sourcePage || "Global Consultation Request"
            };
            await axios.post(`${port}/api/page-enquiry/`, payload);
            toast.success("Enquiry submitted successfully! Our specialists will contact you shortly.");
            setFormData({});
            onClose();
        } catch (error) {
            console.error("Error submitting enquiry:", error);
            toast.error("Failed to submit enquiry. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getHeadingText = () => {
        if (!sourcePage || sourcePage === "General") return "Book Strategic Workforce Consultation";
        if (sourcePage.toLowerCase().includes('consultation') || sourcePage.toLowerCase().includes('request')) {
            return sourcePage;
        }
        return `Connect With Our ${sourcePage} Specialists`;
    };

    const renderFieldInput = (field) => {
        const lowerField = field.toLowerCase();
        
        if (lowerField === 'industry type') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Industry</option>
                    <option value="IT & Software">IT & Software</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="BFSI">BFSI</option>
                    <option value="Ecommerce & Retail">Ecommerce & Retail</option>
                    <option value="Logistics & Supply Chain">Logistics & Supply Chain</option>
                    <option value="Startups">Startups</option>
                    <option value="GCCs">GCCs</option>
                    <option value="Other">Other</option>
                </select>
            );
        }
        
        if (lowerField === 'workforce size' || lowerField === 'team size') {
            const isWorkforce = lowerField === 'workforce size';
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Size</option>
                    {isWorkforce ? (
                        <>
                            <option value="1-20 employees">1-20 employees</option>
                            <option value="20-100 employees">20-100 employees</option>
                            <option value="100-500 employees">100-500 employees</option>
                            <option value="500+ employees">500+ employees</option>
                        </>
                    ) : (
                        <>
                            <option value="1-5 people">1-5 people</option>
                            <option value="5-20 people">5-20 people</option>
                            <option value="20-50 people">20-50 people</option>
                            <option value="50+ people">50+ people</option>
                        </>
                    )}
                </select>
            );
        }

        if (lowerField === 'number of positions') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Count</option>
                    <option value="1-10 positions">1-10 positions</option>
                    <option value="10-50 positions">10-50 positions</option>
                    <option value="50-100 positions">50-100 positions</option>
                    <option value="100+ positions">100+ positions</option>
                </select>
            );
        }

        if (lowerField === 'current hiring volume') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Volume</option>
                    <option value="1-5 hires / month">1-5 hires / month</option>
                    <option value="5-20 hires / month">5-20 hires / month</option>
                    <option value="20-50 hires / month">20-50 hires / month</option>
                    <option value="50+ hires / month">50+ hires / month</option>
                </select>
            );
        }

        if (lowerField === 'hiring timeline') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Timeline</option>
                    <option value="Immediate (Within 15 days)">Immediate (Within 15 days)</option>
                    <option value="Within 30 days">Within 30 days</option>
                    <option value="1-3 months">1-3 months</option>
                    <option value="Proactive / Pipeline">Proactive / Pipeline</option>
                </select>
            );
        }

        if (lowerField === 'support requirement') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Support Type</option>
                    <option value="Ongoing Monthly Support">Ongoing Monthly Support</option>
                    <option value="One-Time Project Support">One-Time Project Support</option>
                    <option value="Consulting / Advisory Only">Consulting / Advisory Only</option>
                    <option value="Other">Other</option>
                </select>
            );
        }

        if (lowerField === 'workforce goal') {
            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Goal</option>
                    <option value="Scalable Hiring">Workforce Scalability</option>
                    <option value="Hiring Speed">Improve Sourcing Speed</option>
                    <option value="Payroll & Compliance">Payroll & Compliance Outsourcing</option>
                    <option value="Integrated HR Systems">Integrated HR Operations</option>
                    <option value="Consulting Advisory">Workforce Strategy Consulting</option>
                </select>
            );
        }

        if (
            lowerField === 'hiring requirement' ||
            lowerField === 'hr requirement' ||
            lowerField === 'payroll requirement' ||
            lowerField === 'compliance requirement' ||
            lowerField === 'staffing requirement'
        ) {
            const pageLower = (sourcePage || '').toLowerCase();
            let options = [];
            
            if (pageLower.includes('specialized')) {
                options = [
                    "Executive Search",
                    "GCC Hiring",
                    "Startup Hiring",
                    "Campus Recruitment",
                    "Remote Hiring",
                    "Offshore Recruitment",
                    "Virtual Recruitment",
                    "AI Recruitment",
                    "Other"
                ];
            } else if (pageLower.includes('rpo')) {
                options = [
                    "End-to-End Recruitment Outsourcing",
                    "Project-Based Hiring Support",
                    "Enterprise Recruitment Management",
                    "Startup Workforce Scaling",
                    "GCC Hiring Support",
                    "High-Volume Talent Acquisition",
                    "Other"
                ];
            } else if (pageLower.includes('outsourcing')) {
                options = [
                    "HR Administration Services",
                    "HR Documentation Services",
                    "HR Compliance Services",
                    "Other"
                ];
            } else if (pageLower.includes('payroll')) {
                options = [
                    "Payroll Processing Services",
                    "Payroll Outsourcing Services",
                    "Contractor Payroll Management Services",
                    "Other"
                ];
            } else if (pageLower.includes('statutory') || pageLower.includes('compliance')) {
                options = [
                    "Statutory Compliance Services",
                    "PF & ESI Management Services",
                    "Other"
                ];
            } else {
                options = [
                    "Contract Staffing",
                    "Permanent Staffing",
                    "IT Staffing",
                    "Non-IT Staffing",
                    "Bulk Hiring",
                    "Other"
                ];
            }

            return (
                <select
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    required
                    className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm font-medium cursor-pointer"
                >
                    <option value="" disabled>Select Requirement</option>
                    {options.map((opt, idx) => (
                        <option key={idx} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        }

        return (
            <input 
                type={lowerField.includes('email') ? 'email' : lowerField.includes('phone') ? 'tel' : 'text'} 
                name={field}
                value={formData[field] || ''}
                onChange={handleChange}
                required={['full name', 'business email', 'phone number', 'email', 'contact number'].includes(lowerField)}
                placeholder={field} 
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400 text-sm font-medium" 
            />
        );
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-[#01014c]/40 backdrop-blur-sm"
                    />
                    
                    {/* Modal container */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: "spring", duration: 0.5 }}
                        className="relative bg-white border border-slate-200 rounded-[2.5rem] p-8 md:p-12 shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto z-10 custom-scrollbar text-left text-slate-900"
                    >
                        {/* Close button */}
                        <button 
                            onClick={onClose}
                            className="absolute top-6 right-6 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-2.5 rounded-full transition-colors z-20"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Heading */}
                        <div className="max-w-2xl mb-8 pr-12">
                            <h2 className="text-2xl md:text-3xl font-bold text-[#01014C] syne mb-3">
                                {getHeadingText()}
                            </h2>
                            <p className="text-slate-500 leading-relaxed text-sm md:text-base">
                                Share your specific requirements with our team of specialists today to customize a workforce strategy aligned with your business goals.
                            </p>
                        </div>

                        {/* Form */}
                        <form className="grid grid-cols-1 md:grid-cols-2 gap-5" onSubmit={handleSubmit}>
                            {regularFields.map((field, i) => (
                                <div key={i} className="space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{field}</label>
                                    {renderFieldInput(field)}
                                </div>
                            ))}
                            
                            {hasMessage && (
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Message</label>
                                    <textarea 
                                        name="Message"
                                        value={formData["Message"] || ''}
                                        onChange={handleChange}
                                        required
                                        rows={3}
                                        placeholder="Brief details about your requirements..." 
                                        className="w-full bg-white border border-slate-300 rounded-xl p-4 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400 text-sm font-medium" 
                                    />
                                </div>
                            )}
                            
                            <div className="md:col-span-2 flex justify-end pt-4">
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wider text-xs rounded-xl transition-all duration-300 disabled:opacity-50 shadow-md shadow-blue-200"
                                >
                                    {loading ? "Submitting..." : "Submit Enquiry"}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default GlobalEnquiryModal;
